import { Suspense, Component, useRef, useState, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Environment, Lightformer, ContactShadows, useProgress } from "@react-three/drei";
import { KitchenRun, RUN_W } from "./KitchenRun";
import { useSurfaceTextures, handleKind } from "./materials";

const SWAY = 0.55, SWAY_SPEED = 0.12, IDLE_MS = 3500, BASE_DIST = 4.8;

/** Pulls the camera back on narrow/tall viewports so the whole run stays in frame. */
const useFitDistance = () => {
  const aspect = useThree((s) => s.viewport.aspect);
  return BASE_DIST * Math.max(1, Math.min(1.9, 1.75 / aspect));
};

/** Drag-to-orbit; when idle the camera slowly sways side to side, resuming from wherever the user left it. */
const Controls = () => {
  const ref = useRef();
  const idle = useRef(true);
  const phase = useRef(0);
  const timer = useRef();
  const camera = useThree((s) => s.camera);
  const dist = useFitDistance();
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const dir = camera.position.clone().sub(c.target).normalize();
    camera.position.copy(c.target).addScaledVector(dir, dist);
    c.update();
  }, [dist, camera]);
  useFrame((_, dt) => {
    const c = ref.current;
    if (!c || !idle.current) return;
    phase.current += dt * SWAY_SPEED;
    c.setAzimuthalAngle(Math.sin(phase.current) * SWAY);
    c.update();
  });
  const onStart = () => { clearTimeout(timer.current); idle.current = false; };
  const onEnd = () => {
    timer.current = setTimeout(() => {
      const az = Math.max(-SWAY, Math.min(SWAY, ref.current?.getAzimuthalAngle() ?? 0));
      phase.current = Math.asin(az / SWAY);
      idle.current = true;
    }, IDLE_MS);
  };
  return (
    <OrbitControls ref={ref} target={[0, 1.15, -0.3]} enablePan={false} enableDamping dampingFactor={0.08}
      minDistance={3.2} maxDistance={9} minPolarAngle={1.15} maxPolarAngle={1.6} minAzimuthAngle={-1.1} maxAzimuthAngle={1.1}
      onStart={onStart} onEnd={onEnd} />
  );
};

const Room = () => (
  <group>
    <mesh rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
      <planeGeometry args={[14, 14]} />
      <meshStandardMaterial color="#17171b" roughness={0.5} metalness={0.05} />
    </mesh>
    <mesh position={[0, 1.7, -0.59]} receiveShadow>
      <planeGeometry args={[16, 3.4]} />
      <meshStandardMaterial color="#26262c" roughness={0.95} />
    </mesh>
  </group>
);

const Scene = ({ board, stone, hw }) => {
  const { boardTex, stoneTex } = useSurfaceTextures(board, stone);
  return <KitchenRun boardTex={boardTex} stoneTex={stoneTex} hw={hw} />;
};

class TextureBoundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidUpdate(prev) { if (prev.resetKey !== this.props.resetKey && this.state.failed) this.setState({ failed: false }); }
  render() { return this.state.failed ? <KitchenRun boardTex={null} stoneTex={null} hw={this.props.hw} /> : this.props.children; }
}

const Loader = () => {
  const { active } = useProgress();
  return active ? (
    <div className="absolute inset-x-0 top-4 flex justify-center pointer-events-none" data-testid="pair-preview-loading">
      <span className="font-mono-j text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] bg-[#0D0D11]/80 px-4 py-2 border border-[#D4AF37]/30">Rendering finishes…</span>
    </div>
  ) : null;
};

/** Exposes a function that re-renders the current frame and returns the WebGL canvas (for snapshots). */
const CaptureBridge = ({ captureRef }) => {
  const { gl, scene, camera } = useThree();
  captureRef.current = () => { gl.render(scene, camera); return gl.domElement; };
  return null;
};

export const Kitchen3D = ({ board, stone, handle, finish, captureRef }) => {
  const hw = handle ? { kind: handleKind(handle.style), finish } : null;
  const [hint, setHint] = useState(true);
  const resetKey = `${board?.id}|${stone?.id}`;
  return (
    <div className="absolute inset-0" onPointerDown={() => setHint(false)}>
      <Canvas shadows dpr={[1, 2]} camera={{ position: [0.8, 1.7, BASE_DIST], fov: 30 }} gl={{ antialias: true, powerPreference: "high-performance" }}
        style={{ touchAction: "pan-y" }} data-testid="pair-preview-canvas">
        <color attach="background" args={["#0f0f13"]} />
        <fog attach="fog" args={["#0f0f13", 7, 14]} />
        <ambientLight intensity={0.45} />
        <directionalLight position={[2.5, 4, 3]} intensity={1.6} castShadow shadow-mapSize={[2048, 2048]} shadow-bias={-0.0004}>
          <orthographicCamera attach="shadow-camera" args={[-3, 3, 3, -3, 0.5, 12]} />
        </directionalLight>
        <spotLight position={[-2.5, 3.2, 2.5]} angle={0.5} penumbra={0.8} intensity={1.0} color="#ffe6bf" />
        <Environment resolution={256}>
          <Lightformer form="rect" intensity={3} position={[0, 3, 4]} scale={[6, 2, 1]} />
          <Lightformer form="rect" intensity={1.2} position={[-4, 2, 1]} rotation={[0, Math.PI / 2, 0]} scale={[3, 2, 1]} color="#ffe9c9" />
          <Lightformer form="rect" intensity={0.8} position={[4, 2, 1]} rotation={[0, -Math.PI / 2, 0]} scale={[3, 2, 1]} color="#cfd8ff" />
        </Environment>
        <Room />
        <Suspense fallback={null}>
          <TextureBoundary resetKey={resetKey} hw={hw}>
            <Scene board={board} stone={stone} hw={hw} />
          </TextureBoundary>
        </Suspense>
        <ContactShadows position={[0, 0.001, 0.2]} opacity={0.6} scale={8} blur={2.2} far={1.5} />
        <Controls />
        {captureRef && <CaptureBridge captureRef={captureRef} />}
      </Canvas>
      <Loader />
      {hint && (
        <p className="absolute right-3 bottom-3 sm:right-4 sm:bottom-4 hidden sm:block font-mono-j text-[10px] uppercase tracking-[0.25em] text-[#A09E97] pointer-events-none" data-testid="pair-preview-hint">
          Drag to orbit · Scroll to zoom
        </p>
      )}
    </div>
  );
};
